import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { keyframes } from '@emotion/react'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import CallEndIcon from '@mui/icons-material/CallEnd'
import InfoIcon from '@mui/icons-material/Info'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded'
import QuizIcon from '@mui/icons-material/Quiz'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import TextsmsIcon from '@mui/icons-material/Textsms'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import TranslateIcon from '@mui/icons-material/Translate'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import DOMPurify from 'dompurify'

import { apiZodios } from '~api/apiZodios'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  LinkIcon,
  ProcessIcon,
  TelephoneIcon,
} from '~assets/icons'
import { images } from '~assets/images'
import { Image } from '~components/common'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import {
  ChatDisplaySource,
  IChatMessageDisplay,
  OrganizationAISettings0,
  ResponseTypeEnum,
  SourceType,
} from '~types'

import { QueryAnalysisDebugInfo } from './QueryAnalysisDebugInfo'
import { RelevantChunksDebugInfo } from './RelevantChunksDebugInfo'

const parseEmailContent = (
  content: string,
): { from: string; to: string; subject: string; body: string } => {
  const fromMatch = content.match(/From: ([^\n]+)/)
  const toMatch = content.match(/To: ([^\n]+)/)
  const subjectMatch = content.match(/Email Subject: ([^\n]+)/)
  const bodyMatch = content.match(/Email Body: (.+)$/s)

  return {
    from: fromMatch?.[1] || '',
    to: toMatch?.[1] || '',
    subject: subjectMatch?.[1] || '',
    body: bodyMatch?.[1] || '',
  }
}

interface MessageBubbleProps {
  message: IChatMessageDisplay
  orgSettings: OrganizationAISettings0
  showDebugInfo: boolean
  isLog?: boolean
  tabIndex?: number
  disableAnimations?: boolean
}

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
})

const processHtml = (html: string): string => {
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const links = doc.getElementsByTagName('a')
  for (let i = 0; i < links.length; i++) {
    const link = links[i]
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  }

  const sanitizedHtml = DOMPurify.sanitize(doc.body.innerHTML)

  DOMPurify.removeHook('afterSanitizeAttributes')

  return sanitizedHtml
}

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`

const initialRenderAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const getActionTypeDeprecated = (text: string): string => {
  if (text.includes('received')) {
    return ResponseTypeEnum.Enum.CALL_RECEIVED
  } else if (text.includes('Transferred')) {
    return ResponseTypeEnum.Enum.CALL_TRANSFERRED
  } else if (text.includes('Ended')) {
    return ResponseTypeEnum.Enum.CALL_ENDED
  } else if (text.includes('language')) {
    return ResponseTypeEnum.Enum.LANGUAGE_CHANGE
  } else if (text.includes('Tool Call')) {
    return ResponseTypeEnum.Enum.TOOL_CALL
  } else if (text.includes('Tool Results')) {
    return ResponseTypeEnum.Enum.TOOL_RESULTS
  } else {
    return 'NONE'
  }
}

export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  (
    { message, orgSettings, showDebugInfo = false, isLog = false, disableAnimations = false },
    ref,
  ) => {
    const organization = orgSettings.organization
    const [impression, setImpression] = useState<boolean | null>(message.impression)
    const isMobile = useMediaQuery(lightTheme.breakpoints.down('md'))
    const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
    const isAI = message.sender === 'AI'
    const isUser = message.sender === 'USER'
    const isAction = message.sender === 'ACTION'
    const actionType =
      message.responseType !== ResponseTypeEnum.Enum.ACTION
        ? message.responseType
        : getActionTypeDeprecated(message.text)
    const contentRef = useRef<HTMLDivElement>(null)
    const [showSources, setShowSources] = useState(false)
    const sourcesContainerRef = useRef<HTMLDivElement>(null)
    const [showRenderedEmail, setShowRenderedEmail] = useState(false)
    const [showInitialAnimation, setShowInitialAnimation] = useState(false)
    const isEmail =
      isUser && message.text.startsWith('From:') && message.text.includes('Email Subject:')
    const superOnlyActionDisplay: string[] = [
      ResponseTypeEnum.Enum.TOOL_CALL,
      ResponseTypeEnum.Enum.TOOL_RESULTS,
      ResponseTypeEnum.Enum.BACKUP_CALL,
    ]
    const sourcesButtonRef = useRef<HTMLButtonElement>(null)
    const darkGreyColor = lightTheme.palette.grey[800]

    const setResponseImpression = async (
      responseId: string | null,
      impression: boolean | null,
    ): Promise<void> => {
      if (!responseId) return
      await apiZodios.ai_setImpression({ response_id: responseId, impression })
      setImpression(impression)
    }

    useEffect(() => {
      if (ref && typeof ref !== 'function' && ref.current) {
        if (!ref.current.hasAttribute('tabindex')) {
          ref.current.setAttribute('tabindex', '0')
        }
      }
    }, [ref])

    useEffect((): (() => void) | void => {
      if (isAI && message.id !== null && !isLog && !disableAnimations) {
        setShowInitialAnimation(true)
        const timer = setTimeout(() => setShowInitialAnimation(false), 2000)
        return () => clearTimeout(timer)
      }
    }, [isAI, message.id, isLog, disableAnimations])

    useEffect(() => {
      // This ensures that even if target="_blank" isn't working properly,
      // we still force links to open in new tabs via click handlers
      if (contentRef.current) {
        const links = contentRef.current.getElementsByTagName('a')
        const eventHandlers: { link: HTMLAnchorElement; handler: (e: Event) => void }[] = []

        for (let i = 0; i < links.length; i++) {
          const link = links[i]
          // Ensure target and rel attributes are set directly
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener noreferrer')

          const clickHandler = (e: Event): void => {
            // We'll still manually handle the click as a fallback
            e.preventDefault()
            const href = link.getAttribute('href')
            if (href) {
              window.open(href, '_blank', 'noopener,noreferrer')
            }
          }

          link.addEventListener('click', clickHandler)
          eventHandlers.push({ link, handler: clickHandler })
        }

        return (): void => {
          eventHandlers.forEach(({ link, handler }) => {
            link.removeEventListener('click', handler)
          })
        }
      }
      // We want this effect to run whenever content changes or is re-rendered
      // Adding contentRef.current as a dependency would cause infinite loops,
      // so we use a more comprehensive set of dependencies
    }, [message.text, message.id, isAI, isAction, showSources, isEmail, showRenderedEmail])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (
          sourcesContainerRef.current &&
          !sourcesContainerRef.current.contains(event.target as Node)
        ) {
          setShowSources(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return (): void => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    const poliApplySources =
      message.sources?.filter((source) => source.is_poli_process === true) || []
    const poliApplyUniqueSources =
      Array.from(new Map(poliApplySources.map((source) => [source.url, source])).values()) || []

    const nonPoliApplySources =
      message.sources?.filter((source) => source.is_poli_process !== true) || []
    const nonPoliApplyUniqueSources = Array.from(
      new Map(nonPoliApplySources.map((source) => [source.url, source])).values(),
    )

    const SourcesButton = styled('button')(({ theme }) => ({
      background: 'none',
      border: 'none',
      padding: theme.spacing(0.5),
      borderRadius: theme.shape.borderRadius,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      '&:focus': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      '&:hover': {
        opacity: 0.8,
      },
    }))

    const SourceLink = ({ source }: { source: ChatDisplaySource }): ReactElement => {
      const hasUrl = Boolean(source?.page_gui_url || source?.page_url)
      const sourceType = source.source_type as SourceType
      const typographyProps = {
        variant: 'caption' as const,
        noWrap: true,
        title: source.title,
        fontWeight: 'bold' as const,
        sx: {
          color: hasUrl ? lightTheme.typography.link.color : darkGreyColor,
          fontSize: '18px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginRight: '4px',
        },
      }

      // If no URL is available, render as plain text
      if (!hasUrl) {
        return (
          <div
            style={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              flex: '1',
              maxWidth: '100%',
              gap: 4,
            }}
          >
            {sourceType === SourceType.SourceTransferLine && (
              <TelephoneIcon color={darkGreyColor} height={16} width={16} />
            )}
            {sourceType === SourceType.SourceFAQ && (
              <QuizIcon height={16} width={16} sx={{ color: darkGreyColor }} />
            )}
            <Typography {...typographyProps}>{source.title}</Typography>
          </div>
        )
      }

      // If URL is available, render with StyledLink
      return (
        <StyledLink
          href={source?.page_gui_url || source?.page_url || undefined}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            flex: '1',
            maxWidth: '100%',
            gap: 1,
          }}
        >
          {sourceType === SourceType.SourceWebsite && (
            <LinkIcon color={darkGreyColor} height={16} width={16} />
          )}
          {sourceType === SourceType.SourceDocument && (
            <DocumentIcon color={darkGreyColor} height={16} width={16} />
          )}
          {sourceType === SourceType.SourcePoliProcess && (
            <ProcessIcon height={16} width={16} color={darkGreyColor} />
          )}
          <Typography {...typographyProps}>{source.title}</Typography>
          <OpenInNewIcon sx={{ color: 'primary.dark', fontSize: 14, flexShrink: 0 }} />
        </StyledLink>
      )
    }

    const OrganizationSourceIndicator = ({
      source,
    }: {
      source: ChatDisplaySource
    }): ReactElement => {
      const [bgcolor, color] = ['var(--offwhite)', 'var(--steel)']
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            className='flex-v-align'
            gap={1}
            sx={{
              backgroundColor: bgcolor,
              width: 'fit-content',
              padding: '5px 10px',
              borderRadius: '5px',
            }}
          >
            {source.is_linked_organization && (
              <LinkIcon color={darkGreyColor} height={16} width={16} />
            )}
            <Typography variant={'caption'} fontWeight={'bold'} sx={{ color: color }}>
              {source.organization_name}
            </Typography>
          </Box>
        </Box>
      )
    }

    const hasNonPoliSources = isAI && nonPoliApplySources.length > 0

    const ConversationBubble = ({}): ReactElement => {
      return (
        <Paper
          ref={ref}
          role='article'
          aria-label={`Message from ${isAI ? 'AI Assistant' : 'User'}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              // Toggle sources if they exist
              if (hasNonPoliSources && !showSources) {
                setShowSources(true)
              }
            }
          }}
          sx={{
            position: 'relative',
            maxWidth: '85%',
            alignSelf: isAI ? 'flex-start' : 'flex-end',
            backgroundColor: isAI
              ? 'background.paper'
              : isLog && isEmail
                ? 'background.paper'
                : 'primary.dark',
            color: isAI
              ? 'text.primary'
              : isLog && isEmail
                ? 'text.primary'
                : 'primary.contrastText',
            borderRadius: 2,
            border: isLog && isEmail ? '1px solid' : 'none',
            borderColor: 'primary.main',
            p: 2,
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            '&:hover': {
              cursor: hasNonPoliSources ? 'pointer' : 'default',
            },
          }}
        >
          <Box
            sx={{
              '& a': {
                ...lightTheme.typography.link,
                fontSize: '18px',
                textDecoration: 'none',
                target: '_blank',
                rel: 'noopener noreferrer',
                '&:hover': {
                  textDecoration: 'underline',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              },
              '& p': {
                display: 'block',
                margin: '1.5 0',
                lineHeight: 1.6,
                fontSize: 18,
              },
              '& div': {
                fontSize: 18,
              },
              '& li': {
                marginBottom: 1,
                lineHeight: 1.6,
                fontSize: 18,
              },
              '& li p': {
                display: 'inline',
                margin: 0,
                fontSize: 18,
              },
              '& ul, & ol': {
                marginTop: 1.5,
                marginBottom: 1.5,
                paddingLeft: 3,
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                margin: '3 0 1.5',
                lineHeight: 1.3,
              },
              '& h1': { fontSize: 24 },
              '& h2': { fontSize: 20 },
              '& h3': { fontSize: 18 },
              '& blockquote': {
                borderLeft: '4px solid #e0e0e0',
                margin: '2 0',
                padding: '1 0 1 2',
                fontStyle: 'italic',
                color: 'text.secondary',
              },
              '& code': {
                backgroundColor: '#f5f5f5',
                padding: '0.5 1',
                borderRadius: '3px',
                fontSize: 14,
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: '4px',
                overflow: 'auto',
                margin: '2 0',
              },
              '& pre code': {
                padding: 0,
                backgroundColor: 'transparent',
              },
            }}
          >
            <div ref={contentRef} className='prose max-w-none'>
              {isEmail ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton
                      size='small'
                      onClick={() => setShowRenderedEmail(!showRenderedEmail)}
                      sx={{ color: 'inherit', opacity: 0.8 }}
                    >
                      {showRenderedEmail ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Box>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: showRenderedEmail
                        ? message.text
                        : processHtml(parseEmailContent(message.text).body),
                    }}
                  />
                </>
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: isAction ? message.text : processHtml(message.text),
                  }}
                />
              )}
            </div>
          </Box>
          {hasNonPoliSources && (
            <Box mt={2}>
              <Divider />
              <SourcesButton
                ref={sourcesButtonRef}
                role='button'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  // Set state in a callback to ensure we have the latest state
                  setShowSources((prevState) => {
                    const newShowSources = !prevState

                    // Use setTimeout to ensure this runs after state update and re-render
                    setTimeout(() => {
                      if (sourcesButtonRef.current) {
                        sourcesButtonRef.current.focus()
                      }
                    }, 0)

                    return newShowSources
                  })
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()

                    // Set state in a callback to ensure we have the latest state
                    setShowSources((prevState) => {
                      const newShowSources = !prevState

                      // Use setTimeout to ensure this runs after state update and re-render
                      setTimeout(() => {
                        if (sourcesButtonRef.current) {
                          sourcesButtonRef.current.focus()
                        }
                      }, 0)

                      return newShowSources
                    })
                  }
                }}
                aria-expanded={showSources}
                aria-controls={`sources-content-${message.id}`}
                aria-label={`View Sources ${showSources ? 'hide' : 'show'}`}
                sx={{
                  mt: 1,
                }}
              >
                <Box mt={0.5} gap={1} className='flex-v-align' aria-hidden='true'>
                  <InfoIcon sx={{ fontSize: 20 }} />
                  <Typography
                    variant='caption'
                    fontWeight='bold'
                    sx={{ ml: 0.5, color: 'primary.dark', display: 'block', fontSize: '16px' }}
                  >
                    View Sources:
                  </Typography>
                  {showSources ? (
                    <Box ml={1} aria-hidden='true'>
                      <ChevronUpIcon color={lightTheme.palette.primary.dark} />
                    </Box>
                  ) : (
                    <Box ml={1} aria-hidden='true'>
                      <ChevronDownIcon color={lightTheme.palette.primary.dark} />
                    </Box>
                  )}
                </Box>
              </SourcesButton>

              <Collapse in={showSources}>
                <Box id={`sources-content-${message.id}`} tabIndex={-1}>
                  {(showDebugInfo ? nonPoliApplySources : nonPoliApplyUniqueSources).map(
                    (source, index) => (
                      <Box
                        key={index}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Box
                          className='flex-v-align'
                          sx={{
                            display: 'flex',
                            justifyContent: 'left',
                            gap: 1,
                          }}
                        >
                          <SourceLink source={source} />
                          {!isMobile && (
                            <>
                              <OrganizationSourceIndicator source={source} />
                              {showDebugInfo && (
                                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                  Score: {source.relevance_score}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>
      )
    }

    const ActionBubble = ({}): ReactElement => {
      const shouldDisplayAction = isSuper || !superOnlyActionDisplay.includes(actionType || 'NONE')

      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {shouldDisplayAction && (
            <Chip
              icon={
                actionType === ResponseTypeEnum.Enum.CALL_ENDED ? (
                  <CallEndIcon color='error' />
                ) : actionType === ResponseTypeEnum.Enum.CALL_RECEIVED ? (
                  <PhoneCallbackIcon color='success' />
                ) : actionType === ResponseTypeEnum.Enum.CALL_TRANSFERRED ? (
                  <PhoneForwardedIcon color='primary' />
                ) : actionType === ResponseTypeEnum.Enum.SMS_SENT ? (
                  <TextsmsIcon color='primary' />
                ) : actionType === ResponseTypeEnum.Enum.LANGUAGE_CHANGE ? (
                  <TranslateIcon color='primary' />
                ) : actionType === ResponseTypeEnum.Enum.BACKUP_CALL ? (
                  <ReplayOutlinedIcon color='warning' />
                ) : actionType === ResponseTypeEnum.Enum.TOOL_CALL ||
                  actionType === ResponseTypeEnum.Enum.TOOL_RESULTS ? (
                  <BuildOutlinedIcon color='secondary' />
                ) : (
                  <></>
                )
              }
              label={stripHtml(message.text)}
              size='medium'
              sx={{
                color: 'text.primary',
                py: 1.5,
                px: 2,
                bgcolor: (theme) => {
                  let color
                  if (actionType === ResponseTypeEnum.Enum.CALL_ENDED) {
                    color = theme.palette.error.main
                  } else if (actionType === ResponseTypeEnum.Enum.CALL_RECEIVED) {
                    color = theme.palette.success.main
                  } else if (
                    actionType === ResponseTypeEnum.Enum.CALL_TRANSFERRED ||
                    actionType === ResponseTypeEnum.Enum.LANGUAGE_CHANGE ||
                    actionType === ResponseTypeEnum.Enum.SMS_SENT
                  ) {
                    color = theme.palette.primary.main
                  } else if (actionType === ResponseTypeEnum.Enum.BACKUP_CALL) {
                    color = theme.palette.warning.main
                  } else {
                    color = theme.palette.grey[500]
                  }
                  return alpha(color, 0.12)
                },
                fontSize: '16px',
              }}
            />
          )}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          position: 'relative',
        }}
      >
        {isAction ? <ActionBubble /> : <ConversationBubble />}

        {poliApplyUniqueSources.length > 0 && (
          <Box sx={{ mt: 1, maxWidth: '85%' }}>
            {poliApplyUniqueSources.map((source, index) => (
              <StyledLink
                key={index}
                href={source?.page_gui_url || source?.page_url || undefined}
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  border: `3px solid ${alpha(`#${organization.bg_color}`, 0.5)}`,
                  backgroundColor: `${alpha(`#${organization.bg_color}`, 0.1)}`,
                  borderRadius: '8px',
                  marginBottom: '12px',
                  padding: '10px 15px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    marginRight: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Image src={images.logo} size='24px' />
                </Box>
                <Typography variant='body2' sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                  <span style={{ fontWeight: 'normal' }}>Click to Apply: </span>
                  <span style={{ fontWeight: 'bold' }}>{source.title}</span>
                </Typography>
              </StyledLink>
            ))}
          </Box>
        )}

        {isAI && message.id !== null && (
          <Box sx={{ mt: 1, ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() =>
                !isLog && setResponseImpression(message.id, impression === true ? null : true)
              }
              color={impression === true ? 'success' : 'default'}
              aria-label='Helpful'
              sx={{
                ...(showInitialAnimation &&
                  !isLog &&
                  !disableAnimations && {
                    animation: `${initialRenderAnimation} 2s ease-out`,
                  }),
                ...(!isLog &&
                  !disableAnimations && {
                    '&:hover': {
                      animation: `${bounceAnimation} 0.6s ease-in-out`,
                    },
                  }),
              }}
            >
              <ThumbUpOffAltIcon />
            </IconButton>
            <IconButton
              onClick={() =>
                !isLog && setResponseImpression(message.id, impression === false ? null : false)
              }
              color={impression === false ? 'warning' : 'default'}
              aria-label='Not Helpful'
              sx={{
                ...(showInitialAnimation &&
                  !isLog &&
                  !disableAnimations && {
                    animation: `${initialRenderAnimation} 2s ease-out`,
                  }),
                ...(!isLog &&
                  !disableAnimations && {
                    '&:hover': {
                      animation: `${bounceAnimation} 0.6s ease-in-out`,
                    },
                  }),
              }}
            >
              <ThumbDownOffAltIcon />
            </IconButton>
          </Box>
        )}
        {!isAI && showDebugInfo && message.queryAnalysis && (
          <QueryAnalysisDebugInfo queryAnalysis={message.queryAnalysis} />
        )}
        {(isAI || isAction) &&
          showDebugInfo &&
          message.relevantChunks &&
          message.relevantChunks.length > 0 && (
            <RelevantChunksDebugInfo relevantChunks={message.relevantChunks} />
          )}
        {!isLog && impression === true && (
          <Alert severity='success' sx={{ mt: 1 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(orgSettings.upvote_custom_text || 'Glad we could help!'),
              }}
            />
          </Alert>
        )}
        {!isLog && impression === false && (
          <Alert severity='info' sx={{ mt: 1 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  orgSettings.downvote_custom_text ||
                    'Sorry this answer was not helpful. Try wording your question differently. The more details the better. Or click through the sources listed.',
                ),
              }}
            />
          </Alert>
        )}
      </Box>
    )
  },
)
